import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AwsCloudFormation } from '@/components/chariot-ui';
import { ConfigIniParser } from 'config-ini-parser';
import React, { PropsWithChildren } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { NonAuthenticatedRoute } from '@/app/constant';
import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/Button';
import { LoginHero } from '@/sections/signup/LoginHero';
import { emptyAuth, useAuth } from '@/state/auth';
import { BackendStack } from '@/types';
import { cn } from '@/utils/classname';
import { generatePathWithSearch } from '@/utils/url.util';

interface Props extends PropsWithChildren {
  title: string;
  description?: React.ReactNode;
  showBack?: boolean;
}

export const PageWrapper = ({
  title = 'Sign in',
  description = '',
  children,
  showBack = false,
}: Props) => {
  const configIniParser = new ConfigIniParser();
  const { currentTheme } = useTheme();
  const { setCustomStack, backend, setCredentials, setSignupStepIndex } =
    useAuth();
  const navigate = useNavigate();

  const processFile = (files: File[]) => {
    const file = files[0];

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const config = configIniParser.parse(e?.target?.result as string);
        const sections = config.sections();

        if (sections.length > 0) {
          const backend = sections.shift() ?? '';

          const creds: BackendStack = {
            backend: config.get(backend, 'name').trim() as string,
            clientId: config.get(backend, 'client_id').trim() as string,
            api: config.get(backend, 'api').trim() as string,
            userPoolId: config.get(backend, 'user_pool_id').trim() as string,
          };

          let loginCreds: { username: string; password: string } = {
            username: '',
            password: '',
          };

          const hasLoginCreds =
            config.isHaveOption(backend, 'username') &&
            config.isHaveOption(backend, 'password');

          if (hasLoginCreds) {
            const username = config.get(backend, 'username').trim() as string;
            const password = config.get(backend, 'password').trim() as string;

            if (username && password) {
              loginCreds = {
                username,
                password,
              };
            }
          }

          setCustomStack(creds, loginCreds);
        }
      } catch (error) {
        if (error instanceof Error && error.message) {
          toast.error('Invalid keychain format');
        }
      }
    };
    reader.readAsText(file);
  };

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    onDrop: processFile,
    useFsAccessApi: false,
    noClick: true,
  });

  const dropzoneProps = {
    ...getRootProps(),
  };

  return (
    <div
      data-theme={currentTheme}
      className="bg-chariot-background-primary flex size-full"
    >
      <div
        className={cn(
          `flex basis-full flex-col items-start justify-center gap-4 overflow-auto md:gap-8 ${isDragAccept ? 'bg-chariot-background-secondary border-2 border-dashed' : 'bg-chariot-background-primary'} p-8 md:basis-1/2 md:p-16 xl:basis-2/5`,
          showBack && 'pt-2 md:pt-4'
        )}
        {...dropzoneProps}
      >
        <input {...getInputProps()} data-testid="keychain-input" />
        {showBack && (
          <Button
            styleType="text"
            className="px-0 py-3 hover:bg-transparent"
            startIcon={<ArrowLeftIcon className="size-5" />}
            onClick={() => {
              setCredentials({ username: '', password: '' });
              setSignupStepIndex(0);
              navigate(
                generatePathWithSearch({
                  pathname: NonAuthenticatedRoute.LOGIN,
                })
              );
            }}
          >
            Back to Login
          </Button>
        )}
        <img
          src="/icons/praetorian-logo-light-mode.svg"
          alt="Praetorian"
          className="h-8 w-auto dark:hidden"
        />
        <img
          src="/icons/praetorian-logo-dark-mode.svg"
          alt="Praetorian"
          className="hidden h-8 w-auto dark:block"
        />

        <h1 className="text-3xl font-bold">{title}</h1>
        {description && <p className="text-sm">{description}</p>}
        {backend !== emptyAuth.backend && (
          <div className="bg-brand-lighter text-brand flex items-center gap-2 rounded p-2 text-sm">
            <AwsCloudFormation className="inline size-6" />
            <span className="font-semibold">{`Stack : ${backend}`}</span>
            <XMarkIcon
              className="bg-chariot-background-secondary ml-auto size-5 cursor-pointer rounded-full"
              onClick={() => setCustomStack()}
            />
          </div>
        )}
        {children}
      </div>
      <LoginHero />
    </div>
  );
};
